import { useEffect } from 'react'
import { useMotionValue, useSpring } from 'framer-motion'

/**
 * Tracks the mouse position with smoothed spring values.
 * Returns normalized values in [-1, 1] (centered at the middle of viewport)
 * plus the raw client X/Y. Useful for parallax + magnetic interactions.
 *
 * Honors `prefers-reduced-motion` by snapping the springs to instant.
 */
export function useMouseSpring() {
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const xNorm = useMotionValue(0)
  const yNorm = useMotionValue(0)

  const sx = useSpring(x, { stiffness: 80, damping: 22, mass: 0.6 })
  const sy = useSpring(y, { stiffness: 80, damping: 22, mass: 0.6 })
  const snx = useSpring(xNorm, { stiffness: 60, damping: 18, mass: 0.5 })
  const sny = useSpring(yNorm, { stiffness: 60, damping: 18, mass: 0.5 })

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      x.set(e.clientX)
      y.set(e.clientY)
      const w = window.innerWidth || 1
      const h = window.innerHeight || 1
      xNorm.set((e.clientX / w) * 2 - 1)
      yNorm.set((e.clientY / h) * 2 - 1)
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => window.removeEventListener('pointermove', onMove)
  }, [x, y, xNorm, yNorm])

  return { x: sx, y: sy, xNorm: snx, yNorm: sny }
}
